import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, ArrowLeft, Sparkles, CalendarPlus, FileDown, CheckCircle2, Circle, History, Flag, ArrowRight, Video } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { MISSION_STATUTS } from './AdrnsMissions';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LIV_STATUT = { a_faire: 'bg-stone-100 text-stone-600', en_cours: 'bg-amber-50 text-amber-700', soumis: 'bg-violet-50 text-violet-700', valide: 'bg-emerald-50 text-emerald-700' };
const AUDIT_LABELS = {
    'mission.created': 'Mission ouverte', 'mission.plan_ia': 'Plan de mission généré par l\'IA',
    'mission.updated': 'Mission mise à jour', 'mission.reunion_planifiee': 'Réunion planifiée',
    'mission.compte_rendu': 'Compte-rendu rédigé', 'mission.cloturee': 'Mission clôturée',
    'livrable.updated': 'Livrable mis à jour', 'livrable.ai_draft': 'Premier jet IA généré',
    'livrable.porteur_submitted': 'Formulaire soumis par le porteur', 'livrable.porteur_saved': 'Brouillon porteur enregistré',
    'document.uploaded': 'Document ajouté', 'intelligence.generated': 'Analyse 360° générée',
    'dossier.updated': 'Dossier mis à jour', 'intervention.added': 'Intervention enregistrée',
};

export default function AdrnsMissionDetail() {
    const { id, mid } = useParams();
    const navigate = useNavigate();
    const { session } = useAuth();
    const [data, setData] = useState(null);
    const [history, setHistory] = useState(null);
    const [busy, setBusy] = useState('');
    const [reunionOpen, setReunionOpen] = useState(false);
    const [reunion, setReunion] = useState({ titre: '', date: '', ordre_du_jour: '', notifier_porteur: false });
    const [crEdit, setCrEdit] = useState({});
    const hdrs = () => ({ headers: { Authorization: `Bearer ${session.access_token}` } });

    const reload = () => axios.get(`${API}/adrns/missions/${mid}`, hdrs())
        .then(r => setData(r.data))
        .catch(e => { toast.error(e.response?.data?.detail || e.message); navigate(`/app/adrns/dossiers/${id}`); });
    useEffect(() => { if (session) reload(); /* eslint-disable-next-line */ }, [session, mid]);

    if (!data) return <div className="flex justify-center py-20"><Loader2 className="animate-spin" /></div>;
    const { mission: m, dossier, progress, reunions, modules } = data;
    const closed = m.statut === 'cloturee';

    const run = async (key, fn, okMsg) => {
        setBusy(key);
        try { await fn(); if (okMsg) toast.success(okMsg); reload(); }
        catch (e) { toast.error(e.response?.data?.detail || e.message); }
        setBusy('');
    };

    const genPlan = () => run('plan', () => axios.post(`${API}/adrns/missions/${mid}/plan`, {}, hdrs()), 'Plan de mission généré par l\'Intelligence 360°');
    const toggleJalon = (jid) => run('jalon', () => axios.patch(`${API}/adrns/missions/${mid}`, {
        jalons: m.jalons.map(j => j.id === jid ? { ...j, done: !j.done } : j)
    }, hdrs()));
    const cloturer = () => window.confirm('Clôturer la mission ? L\'IA rédigera le rapport de clôture.') &&
        run('cloture', () => axios.post(`${API}/adrns/missions/${mid}/cloture`, {}, hdrs()), 'Mission clôturée — rapport généré');
    const exportPdf = async () => {
        try {
            const r = await axios.get(`${API}/adrns/missions/${mid}/rapport.pdf`, { ...hdrs(), responseType: 'blob' });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement('a'); a.href = url; a.download = `mission_${m.titre}.pdf`; a.click();
            URL.revokeObjectURL(url);
        } catch { toast.error('Export impossible'); }
    };
    const odjIa = () => run('odj', async () => {
        const r = await axios.post(`${API}/adrns/missions/${mid}/reunions/odj-ia`, {}, hdrs());
        setReunion(f => ({ ...f, ordre_du_jour: r.data.ordre_du_jour }));
    }, 'Ordre du jour proposé par l\'IA');
    const createReunion = () => run('reunion', async () => {
        await axios.post(`${API}/adrns/missions/${mid}/reunions`, reunion, hdrs());
        setReunionOpen(false);
        setReunion({ titre: '', date: '', ordre_du_jour: '', notifier_porteur: false });
    }, 'Réunion planifiée — équipe notifiée par email');
    const saveCr = (rid) => run('cr', () => axios.patch(`${API}/adrns/missions/${mid}/reunions/${rid}`, { compte_rendu: crEdit[rid] }, hdrs()), 'Compte-rendu enregistré');
    const loadHistory = () => axios.get(`${API}/adrns/missions/${mid}/historique`, hdrs()).then(r => setHistory(r.data.items)).catch(() => setHistory([]));

    return (
        <div className="space-y-6" data-testid="adrns-mission-detail">
            <button onClick={() => navigate(`/app/adrns/dossiers/${id}`)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="mission-back">
                <ArrowLeft size={14} /> Dossier {dossier.porteur_name}
            </button>
            <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                    <div className="eyebrow mb-1">Mission · {m.type_nom} · {dossier.porteur_name}</div>
                    <h1 className="font-heading text-4xl" data-testid="mission-titre">{m.titre}</h1>
                    {m.objectif && <p className="text-sm text-foreground/70 mt-2 max-w-2xl">{m.objectif}</p>}
                </div>
                <div className="flex items-center gap-2">
                    <span className={`text-xs px-2.5 py-1 rounded-full border font-medium ${(MISSION_STATUTS[m.statut] || {}).cls}`} data-testid="mission-statut">
                        {(MISSION_STATUTS[m.statut] || {}).label}
                    </span>
                    <Button size="sm" variant="outline" onClick={exportPdf} data-testid="mission-export-pdf">
                        <FileDown size={13} className="mr-1.5 text-primary" /> PDF
                    </Button>
                    {!closed && (
                        <Button size="sm" onClick={cloturer} disabled={busy === 'cloture'} className="bg-stone-800 hover:bg-stone-700 text-white" data-testid="mission-cloture-btn">
                            {busy === 'cloture' ? <Loader2 size={13} className="animate-spin" /> : 'Clôturer la mission'}
                        </Button>
                    )}
                </div>
            </div>

            <div className="card-soft p-4 flex items-center gap-4">
                <Progress value={progress.avancement} className="flex-1 h-2" />
                <div className="text-sm font-medium whitespace-nowrap" data-testid="mission-avancement">
                    {progress.avancement}% · {progress.livrables_valides}/{progress.livrables_total} livrables · {progress.jalons_done}/{progress.jalons_total} jalons
                </div>
            </div>

            <div className="card-soft p-6 border-primary/20">
                <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
                    <h2 className="font-heading text-xl flex items-center gap-2"><Sparkles size={17} className="text-primary" /> Plan de mission — Intelligence 360°</h2>
                    {!closed && (
                        <Button size="sm" variant="outline" onClick={genPlan} disabled={busy === 'plan'} data-testid="mission-plan-ia-btn">
                            {busy === 'plan' ? <><Loader2 size={13} className="animate-spin mr-1.5" /> Génération…</> : (m.plan_ia ? 'Régénérer le plan' : 'Générer le plan IA')}
                        </Button>
                    )}
                </div>
                {m.plan_ia ? (
                    <div className="space-y-3">
                        <p className="text-sm leading-relaxed" data-testid="mission-note-cadrage">{m.plan_ia}</p>
                        {m.cadence_reunions && <p className="text-xs text-muted-foreground"><b>Cadence recommandée :</b> {m.cadence_reunions}</p>}
                    </div>
                ) : <p className="text-sm text-foreground/60">Générez la note de cadrage, les jalons datés et la cadence de réunions recommandée.</p>}
                {(m.jalons || []).length > 0 && (
                    <div className="mt-4 space-y-2">
                        {m.jalons.map(j => (
                            <button key={j.id} onClick={() => !closed && toggleJalon(j.id)} disabled={closed}
                                className="w-full flex items-center gap-3 text-left text-sm border border-border rounded-lg px-3 py-2 hover:bg-surface-muted/50"
                                data-testid={`mission-jalon-${j.id}`}>
                                {j.done ? <CheckCircle2 size={16} className="text-emerald-600 shrink-0" /> : <Circle size={16} className="text-muted-foreground shrink-0" />}
                                <span className={`flex-1 ${j.done ? 'line-through text-muted-foreground' : ''}`}>{j.titre}</span>
                                <span className="text-xs text-muted-foreground flex items-center gap-1"><Flag size={11} /> {j.echeance}</span>
                            </button>
                        ))}
                    </div>
                )}
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
                <div className="card-soft p-6">
                    <h2 className="font-heading text-xl mb-4">Livrables de la mission</h2>
                    <div className="space-y-1.5 max-h-96 overflow-y-auto">
                        {progress.livrables.map((x, i) => (
                            <Link key={i} to={`/app/adrns/dossiers/${id}/module/${x.module_id}`}
                                className="flex items-center gap-3 text-sm border border-border rounded-lg px-3 py-2 hover:border-primary/40"
                                data-testid={`mission-livrable-${x.key}`}>
                                <span className="text-xs text-muted-foreground w-8">M{x.module_id}</span>
                                <span className="flex-1 truncate">{x.title}</span>
                                <span className={`text-[10px] px-2 py-0.5 rounded-full ${LIV_STATUT[x.statut]}`}>{x.statut.replace('_', ' ')}</span>
                                <ArrowRight size={12} className="text-muted-foreground" />
                            </Link>
                        ))}
                    </div>
                    <div className="mt-3 text-xs text-muted-foreground">Modules mobilisés : {modules.map(mm => `M${mm.id} ${mm.nom}`).join(' · ')}</div>
                </div>

                <div className="card-soft p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="font-heading text-xl">Réunions ({reunions.length})</h2>
                        {!closed && (
                            <Button size="sm" variant="outline" onClick={() => setReunionOpen(true)} data-testid="mission-new-reunion-btn">
                                <CalendarPlus size={13} className="mr-1.5" /> Planifier
                            </Button>
                        )}
                    </div>
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                        {reunions.length === 0 && <p className="text-sm text-foreground/60">Aucune réunion planifiée.</p>}
                        {reunions.map(r => (
                            <div key={r.id} className="border border-border rounded-lg p-3 text-sm" data-testid={`mission-reunion-${r.id}`}>
                                <div className="flex items-center justify-between gap-2">
                                    <span className="font-medium">{r.titre}</span>
                                    <div className="flex items-center gap-2 shrink-0">
                                        {r.jitsi_url && (
                                            <a href={r.jitsi_url} target="_blank" rel="noreferrer"
                                                className="inline-flex items-center gap-1 text-[11px] font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 px-2 py-0.5 rounded-full transition-colors"
                                                data-testid={`mission-reunion-visio-${r.id}`}>
                                                <Video size={11} /> Visio
                                            </a>
                                        )}
                                        <span className="text-xs text-muted-foreground">{new Date(r.date).toLocaleString('fr-FR')}</span>
                                    </div>
                                </div>
                                {r.ordre_du_jour && <p className="text-xs text-foreground/70 mt-1.5 whitespace-pre-wrap">{r.ordre_du_jour}</p>}
                                {r.compte_rendu ? (
                                    <div className="mt-2 border-t border-border pt-2">
                                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Compte-rendu</div>
                                        <p className="text-xs whitespace-pre-wrap">{r.compte_rendu}</p>
                                    </div>
                                ) : !closed && (
                                    <div className="mt-2 space-y-1.5">
                                        <Textarea rows={2} placeholder="Compte-rendu…" value={crEdit[r.id] || ''}
                                            onChange={e => setCrEdit(c => ({ ...c, [r.id]: e.target.value }))} className="text-xs" data-testid={`mission-cr-input-${r.id}`} />
                                        <Button size="sm" variant="outline" onClick={() => saveCr(r.id)} disabled={!(crEdit[r.id] || '').trim()} data-testid={`mission-cr-save-${r.id}`}>Enregistrer le CR</Button>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {m.rapport_cloture && (
                <div className="card-soft p-6 border-emerald-200" data-testid="mission-rapport-cloture">
                    <h2 className="font-heading text-xl mb-3">Rapport de clôture</h2>
                    <div className="text-sm leading-relaxed space-y-1">
                        {m.rapport_cloture.split('\n').map((l, i) => {
                            const t = l.trim();
                            if (!t) return <div key={i} className="h-2" />;
                            if (t.startsWith('#')) return <div key={i} className="font-heading text-base text-primary mt-3">{t.replace(/^#+\s*/, '')}</div>;
                            if (t.startsWith('- ') || t.startsWith('• ')) return <div key={i} className="pl-4 relative"><span className="absolute left-0 text-primary">•</span>{t.slice(2)}</div>;
                            return <p key={i}>{t.replace(/\*\*/g, '')}</p>;
                        })}
                    </div>
                </div>
            )}

            <div className="card-soft p-6">
                <div className="flex items-center justify-between mb-3">
                    <h2 className="font-heading text-xl flex items-center gap-2"><History size={17} className="text-primary" /> Historique de la mission</h2>
                    {history === null && <Button size="sm" variant="outline" onClick={loadHistory} data-testid="mission-history-btn">Afficher</Button>}
                </div>
                {history !== null && (
                    <ul className="space-y-2 max-h-72 overflow-y-auto" data-testid="mission-history-list">
                        {history.length === 0 && <li className="text-sm text-foreground/60">Aucun événement.</li>}
                        {history.map(e => (
                            <li key={e.id} className="flex items-start gap-3 text-sm border-l-2 border-primary/30 pl-3">
                                <div className="flex-1">
                                    <span className="font-medium">{AUDIT_LABELS[e.action] || e.action}</span>
                                    {e.detail?.livrable && <span className="text-foreground/70"> — {e.detail.livrable}</span>}
                                    {e.detail?.titre && <span className="text-foreground/70"> — {e.detail.titre}</span>}
                                    {e.detail?.name && <span className="text-foreground/70"> — {e.detail.name}</span>}
                                </div>
                                <span className="text-xs text-muted-foreground whitespace-nowrap">{new Date(e.at).toLocaleString('fr-FR')}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            <Dialog open={reunionOpen} onOpenChange={setReunionOpen}>
                <DialogContent className="sm:max-w-md" aria-describedby={undefined} data-testid="mission-reunion-dialog">
                    <DialogHeader><DialogTitle className="font-heading">Planifier une réunion</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <Input placeholder="Titre de la réunion *" value={reunion.titre} onChange={e => setReunion(f => ({ ...f, titre: e.target.value }))} data-testid="mission-reunion-titre" />
                        <Input type="datetime-local" value={reunion.date} onChange={e => setReunion(f => ({ ...f, date: e.target.value }))} data-testid="mission-reunion-date" />
                        <div className="space-y-1.5">
                            <Textarea rows={4} placeholder="Ordre du jour" value={reunion.ordre_du_jour} onChange={e => setReunion(f => ({ ...f, ordre_du_jour: e.target.value }))} data-testid="mission-reunion-odj" />
                            <Button size="sm" variant="outline" onClick={odjIa} disabled={busy === 'odj'} data-testid="mission-odj-ia-btn">
                                {busy === 'odj' ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <Sparkles size={13} className="mr-1.5 text-primary" />}
                                Proposer l'ODJ avec l'IA
                            </Button>
                        </div>
                        <label className="flex items-center gap-2 text-sm">
                            <Checkbox checked={reunion.notifier_porteur} onCheckedChange={v => setReunion(f => ({ ...f, notifier_porteur: !!v }))} data-testid="mission-reunion-notif-porteur" />
                            Notifier aussi le porteur par email
                        </label>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setReunionOpen(false)}>Annuler</Button>
                        <Button onClick={createReunion} disabled={busy === 'reunion' || reunion.titre.trim().length < 2 || !reunion.date} data-testid="mission-reunion-save">
                            {busy === 'reunion' ? <Loader2 size={13} className="animate-spin" /> : 'Planifier'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
